import React, { useEffect, useCallback, useRef, useState } from "react";
import { loader } from "helpers";
import useStore from "store";

import { Link, useHistory } from "react-router-dom";
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
				'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const WEEKDAYS_LONG = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

/* Duración de la animación del panel. El desmontaje se retrasa lo mismo para que
   la salida se vea; si se cambia aquí, cambiarlo también en el CSS. */
const SHEET_ANIMATION_MS = 220;

/* Arrastre hacia abajo a partir del cual el panel se cierra al soltar. */
const SHEET_CLOSE_DRAG_PX = 80;

/* Colores de Bootstrap 5, los mismos que ya usan los botones del menú y las
   listas: verde = abierto/preparado, gris = cerrado, naranja = pendiente,
   azul claro = en preparación, azul = enviado. */
const WORKORDER_COLORS = {
	open: '#198754',
	closed: '#6c757d'
};

const LOGISTICS_COLORS = {
	pendiente_preparar: '#ffc107',
	en_preparacion: '#0dcaf0',
	preparado: '#198754',
	enviado: '#0d6efd'
};

const LOGISTICS_LABELS = {
	pendiente_preparar: 'Pendiente preparar',
	en_preparacion: 'En preparación',
	preparado: 'Preparado',
	enviado: 'Enviado'
};

/* El color solo no basta: un parte abierto y un suministro preparado son verdes
   los dos. La forma es la que distingue el tipo de trabajo (círculo / cuadrado /
   rombo), y va explicada en la leyenda. */
function entryColor(entry) {
	if ( entry.kind === 'workorder' ) return WORKORDER_COLORS[entry.status] ?? '#6c757d';
	return LOGISTICS_COLORS[entry.logistics_status] ?? '#6c757d';
}

/* Día adicional del parte (días sueltos o rangos que se apuntan aparte de la
   fecha prevista + duración): punto hueco en la rejilla y borde discontinuo en la
   ficha del día, como en el calendario web. */
function dotStyle(entry) {
	const color = entryColor(entry);
	if ( entry.additional ) return { background: 'transparent', border: '2px solid ' + color, boxSizing: 'border-box' };
	return { background: color };
}

function entryLink(entry) {
	return entry.kind === 'workorder' ? '/plan-details/' + entry.id : '/logistics-detail/' + entry.id;
}

function entryTypeLabel(entry) {
	if ( entry.kind === 'supply' ) return 'Suministro';
	if ( entry.kind === 'logistics' ) return 'Logística';
	return entry.type === 'new' ? 'Nueva instalación' : 'Mantenimiento';
}

/* "Martes, 28 de julio". Los nombres van en arrays y no por el locale de moment,
   que en la app no está en español y cambiarlo afectaría a las demás pantallas. */
function dayTitle(dateString) {
	const date = moment(dateString);
	return WEEKDAYS_LONG[date.isoWeekday() - 1] + ', ' + date.date() + ' de ' + MONTHS[date.month()].toLowerCase();
}

function entryStatusLabel(entry) {
	if ( entry.kind === 'workorder' ) return entry.status === 'closed' ? 'Cerrado' : 'Abierto';
	return LOGISTICS_LABELS[entry.logistics_status] ?? '';
}

const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const slideUp = keyframes`
	from { transform: translateY(100%); }
	to { transform: translateY(0); }
`;

const ContainerStyled = styled.div`

	#loader {
		width: 100px;
	}

	.page-title {
		text-align: center;
		position: relative;

		#back-button {
			position: absolute;
			left: 0;
			top: 5px;
		}
	}

	.month-nav {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;

		.month-label {
			font-weight: 700;
			font-size: 16px;
		}
	}

	.calendar-grid {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		gap: 3px;

		.weekday {
			text-align: center;
			font-size: 12px;
			font-weight: 700;
			color: #6c757d;
			padding-bottom: 4px;
		}

		.day {
			background: white;
			border-radius: 6px;
			min-height: 52px;
			padding: 4px 2px 2px;
			text-align: center;
			border: 1px solid #eee;

			.day-number {
				font-size: 14px;
				font-weight: 600;
				line-height: 1.1;
			}

			.day-dots {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				justify-content: center;
				gap: 3px;
				margin-top: 4px;
				min-height: 10px;
			}

			.dot {
				width: 8px;
				height: 8px;
				display: inline-block;
			}

			.dot-workorder {
				border-radius: 50%;
			}

			.dot-logistics {
				transform: rotate(45deg);
			}

			.dot-more {
				font-size: 10px;
				font-weight: 700;
				color: #6c757d;
				line-height: 1;
			}

			&.out-of-month {
				background: #f8f9fa;
				border-color: #f1f1f1;

				.day-number {
					color: #c5c5c5;
				}
			}

			&.weekend .day-number {
				color: #adb5bd;
			}

			&.today {
				border-color: var(--bs-primary);
				border-width: 2px;

				.day-number {
					color: var(--bs-primary);
				}
			}

			&.has-work {
				cursor: pointer;
			}
		}
	}

	.legend {
		margin-top: 12px;
		font-size: 12px;
		color: #6c757d;

		.legend-title {
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 1px;
			margin-bottom: 6px;
		}

		.legend-item {
			display: inline-flex;
			align-items: center;
			gap: 5px;
			margin-right: 12px;
			margin-bottom: 4px;
		}

		.legend-dot {
			width: 8px;
			height: 8px;
			display: inline-block;
		}

		.legend-dot.dot-workorder {
			border-radius: 50%;
		}

		.legend-dot.dot-logistics {
			transform: rotate(45deg);
		}
	}

	/* Panel inferior del día. Sin el JS de Bootstrap: se monta al tocar un día y
	   se desmonta con retardo para que la animación de salida se vea. */
	.sheet-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0,0,0,0.5);
		z-index: 1000;
		display: flex;
		align-items: flex-end;
		justify-content: center;
		animation: ${fadeIn} ${SHEET_ANIMATION_MS}ms ease;

		&.closing {
			opacity: 0;
			transition: opacity ${SHEET_ANIMATION_MS}ms ease;
		}
	}

	.bottom-sheet {
		background: white;
		width: 100%;
		max-width: 500px;
		max-height: 85vh;
		border-radius: 16px 16px 0 0;
		box-shadow: 0 -2px 16px rgba(0,0,0,0.2);
		display: flex;
		flex-direction: column;
		animation: ${slideUp} ${SHEET_ANIMATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1);

		/* Respeta la barra de gestos de los móviles sin botones físicos */
		padding-bottom: env(safe-area-inset-bottom, 0px);

		/* En cuanto termina de entrar se apaga la animación: si se dejara puesta,
		   soltar un arrastre corto la relanzaría y el panel volvería a subir desde
		   abajo. A partir de aquí el movimiento lo lleva la transición. */
		&.settled {
			animation: none;
			transition: transform ${SHEET_ANIMATION_MS}ms ease;
		}

		/* Mientras el dedo arrastra manda el transform en línea, sin suavizado */
		&.dragging {
			transition: none;
		}

		&.closing {
			transform: translateY(100%);
			transition: transform ${SHEET_ANIMATION_MS}ms ease-in;
		}

		.sheet-header {
			flex: 0 0 auto;
			padding: 8px 15px 10px;
			border-bottom: 1px solid #eee;
			position: relative;
			/* El arrastre vive solo aquí: dentro de la lista el dedo tiene que scrollar */
			touch-action: none;
			cursor: grab;

			.sheet-handle {
				width: 40px;
				height: 4px;
				border-radius: 2px;
				background: #ced4da;
				margin: 0 auto 10px;
			}

			.sheet-title {
				font-weight: 700;
				font-size: 16px;
				text-align: left;
				padding-right: 30px;
			}

			.sheet-subtitle {
				font-size: 12px;
				color: #6c757d;
				text-align: left;
			}

			.sheet-close {
				position: absolute;
				top: 12px;
				right: 12px;
				background: none;
				border: none;
				font-size: 24px;
				font-weight: bold;
				line-height: 1;
				cursor: pointer;
				color: #6c757d;
			}
		}

		.sheet-body {
			flex: 1 1 auto;
			overflow-y: auto;
			-webkit-overflow-scrolling: touch;
			padding: 12px 15px 15px;
		}

		.day-entry {
			text-align: left;
			border: 1px solid #dee2e6;
			border-left-width: 5px;
			border-radius: 8px;
			padding: 10px 12px;
			margin-bottom: 8px;
			cursor: pointer;

			&:last-child {
				margin-bottom: 0;
			}

			&:active {
				background: #f8f9fa;
			}

			.entry-code {
				font-weight: 700;
				font-size: 14px;
			}

			.entry-row {
				font-size: 13px;
				color: #495057;
			}

			&.is-additional {
				border-style: dashed;
			}

			.entry-additional {
				font-size: 11px;
				font-weight: 700;
				text-transform: uppercase;
				color: #3f6ad8;
				margin-bottom: 2px;
			}
		}
	}
`;

let cancelTokenSource = null;

export default function Calendar() {
	const user = useStore(state => state.user);
	const history = useHistory();

	const [month, setMonth] = useState(moment().format('YYYY-MM'));
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [selectedDay, setSelectedDay] = useState(null);
	const [sheetClosing, setSheetClosing] = useState(false);
	const [sheetSettled, setSheetSettled] = useState(false);
	const [dragY, setDragY] = useState(null);

	const dragStartY = useRef(null);
	const closeTimeout = useRef(null);

	const loadData = useCallback((monthToLoad) => {
		setData(null);
		setLoading(true);

		axios.get('api/workorders/calendar', {
			params: { month: monthToLoad },
			cancelToken: cancelTokenSource.token
		}).then((response) => {
			setData(response.data);
		}).catch((error) => {
			if ( axios.isCancel(error) ) return;
			toast.error('Error al cargar los datos, por favor, vuelve a intentarlo');
		}).then(() => {
			setLoading(false);
		});
	}, []);

	useEffect(() => {
		// Un token nuevo por mes: si el operario cambia de mes antes de que llegue la
		// respuesta anterior, esa respuesta se descarta y no pisa a la buena
		cancelTokenSource = axios.CancelToken.source();

		loadData(month);

		return function cleanup() {
			cancelTokenSource.cancel();
		}
	}, [loadData, month]);

	// El temporizador de cierre sobreviviría al desmontaje y dejaría un setState
	// sobre un componente que ya no existe
	useEffect(() => {
		return function cleanup() {
			if ( closeTimeout.current ) clearTimeout(closeTimeout.current);
		}
	}, []);

	const openSheet = (dateString) => {
		if ( closeTimeout.current ) clearTimeout(closeTimeout.current);
		setSheetClosing(false);
		setSheetSettled(false);
		setDragY(null);
		setSelectedDay(dateString);
	};

	const closeSheet = () => {
		if ( closeTimeout.current ) clearTimeout(closeTimeout.current);
		setDragY(null);
		setSheetClosing(true);

		closeTimeout.current = setTimeout(() => {
			setSelectedDay(null);
			setSheetClosing(false);
			setSheetSettled(false);
			closeTimeout.current = null;
		}, SHEET_ANIMATION_MS);
	};

	// Arrastrar la cabecera hacia abajo cierra el panel, como en cualquier app
	const onDragStart = (e) => {
		dragStartY.current = e.touches[0].clientY;
		setDragY(0);
	};

	const onDragMove = (e) => {
		if ( dragStartY.current === null ) return;
		const delta = e.touches[0].clientY - dragStartY.current;
		setDragY(delta > 0 ? delta : 0);
	};

	const onDragEnd = () => {
		const delta = dragY ?? 0;
		dragStartY.current = null;

		if ( delta > SHEET_CLOSE_DRAG_PX ) closeSheet();
		else setDragY(null);
	};

	const changeMonth = (diff) => {
		closeSheet();
		setMonth(moment(month + '-01').add(diff, 'months').format('YYYY-MM'));
	};

	const goToday = () => {
		closeSheet();
		setMonth(moment().format('YYYY-MM'));
	};

	// Rejilla: 6 semanas completas de lunes a domingo, empezando en el lunes de la
	// semana en la que cae el día 1
	const monthStart = moment(month + '-01');
	const gridStart = monthStart.clone().startOf('isoWeek');
	const today = moment().format('YYYY-MM-DD');

	const days = data?.days ?? {};

	let cells = [];
	for(let i = 0; i < 42; i++) {
		const date = gridStart.clone().add(i, 'days');
		const dateString = date.format('YYYY-MM-DD');
		const entries = days[dateString] ?? [];

		let classes = ['day'];
		if ( date.format('YYYY-MM') !== month ) classes.push('out-of-month');
		if ( date.isoWeekday() > 5 ) classes.push('weekend');
		if ( dateString === today ) classes.push('today');
		if ( entries.length ) classes.push('has-work');

		cells.push(
			<div
				key={dateString}
				className={classes.join(' ')}
				onClick={entries.length ? () => openSheet(dateString) : undefined}
			>
				<div className="day-number">{ date.format('D') }</div>
				<div className="day-dots">
					{ entries.slice(0, 3).map((entry, idx) =>
						<span
							key={idx}
							className={'dot ' + (entry.kind === 'workorder' ? 'dot-workorder' : 'dot-logistics')}
							style={dotStyle(entry)}
						></span>
					) }
					{ entries.length > 3 && <span className="dot-more">+{ entries.length - 3 }</span> }
				</div>
			</div>
		);
	}

	// Ventana del día
	const selectedEntries = selectedDay ? (days[selectedDay] ?? []) : [];

	// Render
	return (
		<ContainerStyled className="container" style={{maxWidth: 500}}>
			<div className="row">
				<div className="col-md-12">
					<div className="page-title">
						<Link to="/" className="btn btn-primary" id="back-button"><FontAwesomeIcon icon={faChevronLeft} /></Link>
						<h4 className="p-0 m-0 py-2">Calendario</h4>
					</div>
				</div>

				<div className="col-md-12 mt-2">
					<div className="month-nav">
						<button className="btn btn-sm btn-outline-secondary" onClick={() => changeMonth(-1)}>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
						<span className="month-label">
							{ MONTHS[monthStart.month()] } { monthStart.format('YYYY') }
						</span>
						<button className="btn btn-sm btn-outline-secondary" onClick={() => changeMonth(1)}>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					</div>

					<div className="calendar-grid">
						{ WEEKDAYS.map((weekday, idx) => <div className="weekday" key={'wd' + idx}>{ weekday }</div>) }
						{ cells }
					</div>

					<div className="text-center mt-2">
						<button className="btn btn-sm btn-outline-primary" onClick={goToday}>Hoy</button>
					</div>

					<div className="legend">
						<div className="legend-title">Leyenda</div>
						{ user?.has_progapp &&
							<>
								<span className="legend-item">
									<span className="legend-dot dot-workorder" style={{background: WORKORDER_COLORS.open}}></span> Parte abierto
								</span>
								<span className="legend-item">
									<span className="legend-dot dot-workorder" style={{background: WORKORDER_COLORS.closed}}></span> Parte cerrado
								</span>
								<span className="legend-item">
									<span className="legend-dot dot-workorder" style={dotStyle({ kind: 'workorder', status: 'open', additional: true })}></span> Día adicional
								</span>
							</>
						}
						{ user?.has_logistica &&
							<>
								<span className="legend-item">
									<span className="legend-dot dot-logistics" style={{background: LOGISTICS_COLORS.pendiente_preparar}}></span> Pendiente preparar
								</span>
								<span className="legend-item">
									<span className="legend-dot dot-logistics" style={{background: LOGISTICS_COLORS.en_preparacion}}></span> En preparación
								</span>
								<span className="legend-item">
									<span className="legend-dot dot-logistics" style={{background: LOGISTICS_COLORS.preparado}}></span> Preparado
								</span>
								<span className="legend-item">
									<span className="legend-dot dot-logistics" style={{background: LOGISTICS_COLORS.enviado}}></span> Enviado
								</span>
							</>
						}
						{ user?.has_progapp && user?.has_logistica &&
							<div className="mt-1">Círculo: parte de trabajo · Rombo: suministro / logística (fecha prevista de entrega)</div>
						}
					</div>

					<div className="text-center">
						{ loading ? loader() : '' }
					</div>
				</div>
			</div>

			{ selectedDay &&
				<div className={'sheet-backdrop' + (sheetClosing ? ' closing' : '')} onClick={closeSheet}>
					<div
						className={'bottom-sheet'
							+ (sheetSettled ? ' settled' : '')
							+ (dragY !== null ? ' dragging' : '')
							+ (sheetClosing ? ' closing' : '')}
						style={dragY ? { transform: 'translateY(' + dragY + 'px)' } : undefined}
						onAnimationEnd={() => setSheetSettled(true)}
						onClick={(e) => e.stopPropagation()}
					>
						<div
							className="sheet-header"
							onTouchStart={onDragStart}
							onTouchMove={onDragMove}
							onTouchEnd={onDragEnd}
							onTouchCancel={onDragEnd}
						>
							<div className="sheet-handle"></div>
							<button className="sheet-close" onClick={closeSheet}>&times;</button>
							<div className="sheet-title">{ dayTitle(selectedDay) }</div>
							<div className="sheet-subtitle">
								{ selectedEntries.length } { selectedEntries.length === 1 ? 'trabajo' : 'trabajos' }
							</div>
						</div>

						<div className="sheet-body">
							{ selectedEntries.map((entry, idx) =>
								<div
									className={'day-entry' + (entry.additional ? ' is-additional' : '')}
									key={idx}
									style={{ borderLeftColor: entryColor(entry) }}
									onClick={() => history.push(entryLink(entry))}
								>
									{ entry.additional &&
										<div className="entry-additional">
											Día adicional{ entry.additional_note ? ' · ' + entry.additional_note : '' }
										</div>
									}
									<div className="entry-code">REF.: { entry.code }</div>
									<div className="entry-row">{ entryTypeLabel(entry) } · { entryStatusLabel(entry) }</div>
									{ entry.client_name && <div className="entry-row"><b>Cliente:</b> { entry.client_name }</div> }
									{ entry.reference && <div className="entry-row"><b>Obra:</b> { entry.reference }</div> }
									{ entry.time && <div className="entry-row"><b>Franja horaria:</b> { entry.time }</div> }
								</div>
							) }
						</div>
					</div>
				</div>
			}
		</ContainerStyled>
	);
}
